/**
 * @description MLflow Experiment 관련 쿼리 키 모음
 *
 * 계층 구조:
 * - all: 모든 experiment 쿼리의 루트 키
 * - experimentsPrefix: 실험 목록 관련 키의 접두사
 * - allExperiments: 모든 활성 실험 목록 키
 * - experimentDetail: 특정 실험 상세 정보 키
 * - experimentRuns: 특정 실험의 실행 목록 키
 *
 * @example
 * ```ts
 * // 모든 실험 조회
 * queryKey: experimentKeys.allExperiments()
 *
 * // 특정 실험 상세 조회
 * queryKey: experimentKeys.experimentDetail(experimentId)
 *
 * // 모든 실험 캐시 무효화
 * queryClient.invalidateQueries({ queryKey: experimentKeys.all })
 * ```
 */
export const experimentKeys = {
  all: ['mlflow'] as const,

  experimentsPrefix: () => [...experimentKeys.all, 'experiments'] as const,
  allExperiments: (search?: string) => [...experimentKeys.experimentsPrefix(), 'all', search || ''] as const,

  experimentDetail: (experimentId: string | undefined) => [...experimentKeys.all, 'experiment', experimentId] as const,

  experimentRunsPrefix: (experimentId: string | undefined) => [...experimentKeys.all, 'runs', experimentId] as const,
  experimentRuns: (experimentId: string | undefined) => [...experimentKeys.experimentRunsPrefix(experimentId)] as const,

  runDetail: (runId: string | undefined) => [...experimentKeys.all, 'run', runId] as const,

  experimentLoggedModelsPrefix: (experimentId: string | undefined) =>
    [...experimentKeys.all, 'loggedModels', experimentId] as const,
  experimentLoggedModels: (experimentId: string | undefined) =>
    [...experimentKeys.experimentLoggedModelsPrefix(experimentId)] as const,

  experimentModelVersionsPrefix: (runIds: string[]) =>
    [...experimentKeys.all, 'modelVersions', [...runIds].sort()] as const,
  experimentModelVersions: (runIds: string[]) => [...experimentKeys.experimentModelVersionsPrefix(runIds)] as const,

  experimentsComparison: (experimentIds: string[]) =>
    [...experimentKeys.all, 'comparison', [...experimentIds].sort()] as const,

  runsComparison: (runIds: number[]) => [...experimentKeys.all, 'runsComparison', [...runIds].sort()] as const,

  assetDetail: (assetId: string | undefined, assetType?: string, runId?: number) =>
    [...experimentKeys.all, 'assetDetail', assetId, assetType || 'run', runId] as const,
} as const;
